import {
  ADVANCED_STATUS_REQUIRED_FIELDS,
  getStatusName,
  isDealFieldFilled,
  REQUIRED_ADVISOR_SERVICES,
  statusIsInClosing,
  statusRequiresAdvancedDealFields,
} from "@/utils/dealValidation";
import type { Deal } from "@/types/deal";

export type TermSheetDialogState = {
  open: boolean;
  rows: Array<{ id: number; dealName: string; missingFields: string[] }>;
};

/** One field of one row, as sent to PATCH /deal/{id} or PATCH /accordions/{id}. */
export type PipelineCellPatch = {
  endpoint: string;
  /** API key the value is saved under (e.g. `source_id` for the Source column). */
  key: string;
  body: {
    changes: Record<string, unknown>;
    expected: Record<string, unknown>;
    client_id: string;
  };
};

export type PipelineFieldConflict = {
  current: unknown;
  changed_by?: string | null;
  changed_at?: string | null;
};

type Row = Record<string, unknown>;

export function isAccordionRow(row: unknown): boolean {
  return (
    !!row &&
    typeof row === "object" &&
    "parent_deal_id" in row &&
    (row as Row).parent_deal_id != null
  );
}

const idOf = (value: unknown): unknown =>
  value && typeof value === "object" && "id" in value
    ? ((value as { id?: unknown }).id ?? null)
    : (value ?? null);

/** Accordion (add-on) rows reuse deal column names; map them to the add-on's own keys. */
const ACCORDION_KEYS: Record<
  string,
  { key: string; toApi?: (v: unknown) => unknown }
> = {
  deal_name: { key: "accordion_name" },
  status: { key: "status_id", toApi: idOf },
};

let pipelineClientId: string | null = null;

/** Random id for this browser tab, echoed back in socket events for its own saves. */
export function getPipelineClientId(): string {
  if (!pipelineClientId) {
    pipelineClientId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `tab-${Math.random().toString(36).slice(2)}-${Date.now()}`;
  }
  return pipelineClientId;
}

/**
 * ``extraChanges`` are saved in the same request without a conflict check
 * (e.g. the kill reason sent with a move to Killed). Deal rows only.
 */
export function buildCellPatch(
  row: Deal,
  field: string,
  newValue: unknown,
  oldValue: unknown,
  extraChanges: Record<string, unknown> = {},
): PipelineCellPatch | null {
  const data = row as unknown as Row;
  const clientId = getPipelineClientId();

  if (isAccordionRow(row)) {
    const accordionId = data.accordion_id;
    if (typeof accordionId !== "number") return null;
    const mapping = ACCORDION_KEYS[field] ?? { key: field };
    const toApi = mapping.toApi ?? ((v: unknown) => v ?? null);
    return {
      endpoint: `accordions/${accordionId}`,
      key: mapping.key,
      body: {
        changes: { [mapping.key]: toApi(newValue) },
        expected: { [mapping.key]: toApi(oldValue) },
        client_id: clientId,
      },
    };
  }

  if (typeof row.id !== "number" || row.id <= 0) return null;

  if (field === "source_entity") {
    // The API stores the source by id (same conversion the bulk save made).
    return {
      endpoint: `deal/${row.id}`,
      key: "source_id",
      body: {
        changes: { source_id: idOf(newValue) },
        expected: { source_id: idOf(oldValue) },
        client_id: clientId,
      },
    };
  }

  return {
    endpoint: `deal/${row.id}`,
    key: field,
    body: {
      changes: { [field]: newValue ?? null, ...extraChanges },
      expected: { [field]: oldValue ?? null },
      client_id: clientId,
    },
  };
}

const REQUIRED_FIELD_LABELS: Record<string, string> = {
  irr_local: "IRR Local",
  irr_usd: "IRR USD",
  irr_local_net: "IRR Local excl. TFC fee",
  irr_usd_net: "IRR USD excl. TFC fee",
};

/**
 * Fields still missing when a row moves into an "advanced" status (term sheet
 * onwards). Empty when the move is allowed.
 */
export function missingFieldsForStatusChange(
  row: Deal,
  newStatus: unknown,
  oldStatus: unknown,
): string[] {
  const next = getStatusName(newStatus).trim().toLowerCase();
  const previous = getStatusName(oldStatus).trim().toLowerCase();
  if (
    !statusRequiresAdvancedDealFields(next) ||
    statusRequiresAdvancedDealFields(previous)
  ) {
    return [];
  }

  const accordion = isAccordionRow(row);
  const data = row as unknown as Row;
  return ADVANCED_STATUS_REQUIRED_FIELDS.filter(
    ({ field }) =>
      !(accordion && field === "cm_rating") && !isDealFieldFilled(data[field]),
  ).map(({ field, label }) => REQUIRED_FIELD_LABELS[field] ?? label);
}

/** Reminder shown (never a block) when a row moves into closing. */
export type AdvisorFeeReminder = {
  dealName: string;
  /** Row id to open: the deal itself, or an add-on's parent deal. */
  dealId: number | null;
  missing: string[];
};

/**
 * Advisor fee quotes to remind about after a saved status change moves a row
 * into closing (Signed / Committed, Preview Memo, IC Memo). `savedRow` is the
 * row the API returned (deal saves only); its `advisor_fees_missing` is used
 * when present, otherwise all standard services are listed.
 */
export function advisorFeeReminderForStatusChange(
  row: Deal,
  newStatus: unknown,
  oldStatus: unknown,
  savedRow?: Deal | null,
): AdvisorFeeReminder | null {
  if (!statusIsInClosing(newStatus) || statusIsInClosing(oldStatus)) {
    return null;
  }
  const fromApi = savedRow?.advisor_fees_missing;
  const missing = Array.isArray(fromApi) ? fromApi : REQUIRED_ADVISOR_SERVICES;
  if (missing.length === 0) return null;
  const dealId = isAccordionRow(row)
    ? (row.parent_deal_id ?? null)
    : typeof row.id === "number" && row.id > 0
      ? row.id
      : null;
  return { dealName: row.deal_name || "This deal", dealId, missing };
}

/** Pull `{conflicts}` out of a 409 ApiResponse message (`detail` object). */
export function conflictFromMessage(
  message: unknown,
  key: string,
): PipelineFieldConflict | null {
  if (!message || typeof message !== "object") return null;
  const conflicts = (
    message as { conflicts?: Record<string, PipelineFieldConflict> }
  ).conflicts;
  return conflicts?.[key] ?? null;
}

/** Short readable form of a cell value for messages. */
export function describeCellValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "(empty)";
  if (Array.isArray(value)) {
    return value.map(describeCellValue).join(", ") || "(empty)";
  }
  if (typeof value === "object") {
    const obj = value as Row;
    for (const key of [
      "name",
      "status",
      "sponsor_name",
      "source_name",
      "nicename",
      "sector",
      "label",
      "kill_reason",
      "loan_type",
    ]) {
      if (typeof obj[key] === "string" && obj[key]) return obj[key] as string;
    }
    return obj.id != null ? String(obj.id) : "(empty)";
  }
  return String(value);
}

/** " 5 min ago" style suffix for a change time (leading space; empty if unknown). */
export function formatChangedAgo(iso: string | null, now = Date.now()): string {
  if (!iso) return "";
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return "";
  const minutes = Math.round((now - at) / 60_000);
  if (minutes < 1) return " just now";
  if (minutes < 60) return ` ${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return ` ${hours} h ago`;
  return ` on ${new Date(at).toLocaleDateString()}`;
}
